import { matchesAssistantCitationMessage } from "./assistantCitationMessageIdentity";
import { toastManager } from "../ui/toast";
import { useEffect, useEffectEvent, useRef, useState } from "react";
import type { AssistantCitationRequest, AssistantCitationTarget } from "./timelineCitationContext";
import type { MessagesTimelineRow } from "./MessagesTimeline.logic";

export function useTimelineCitationTarget({
  request,
  rows,
  viewport,
  pauseFollowing,
}: {
  readonly request: AssistantCitationRequest | null;
  readonly rows: readonly MessagesTimelineRow[];
  readonly viewport: HTMLElement | null;
  readonly pauseFollowing: () => void;
}) {
  const [target, setTarget] = useState<AssistantCitationTarget | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [positionedKey, setPositionedKey] = useState<string | null>(null);
  const requestKey = request?.key;
  const readRequest = useEffectEvent(() => request);
  const pause = useEffectEvent(pauseFollowing);
  useEffect(() => {
    const request = readRequest();
    if (!request || !loaded || !viewport) return;
    pause();
    const next: AssistantCitationTarget = {
      ...request,
      activationRef: { current: { scrolled: false, dismissed: false } },
      onComplete: () => {
        pause();
        setPositionedKey(request.key);
      },
    };
    const activation = next.activationRef.current;
    setTarget(next);
    const cancel = (onlyPending: boolean) => {
      if (onlyPending && activation.scrolled) return;
      activation.dismissed = true;
      activation.cancelScroll?.();
      setTarget(null);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") cancel(false);
      else if (
        viewport.contains(event.target as Node) &&
        ["ArrowUp", "ArrowDown", "PageUp", "PageDown", "Home", "End", " "].includes(event.key)
      )
        cancel(true);
    };
    const onWheel = (event: WheelEvent) => {
      if (event.deltaY !== 0) cancel(true);
    };
    const onNavigation = () => cancel(true);
    viewport.addEventListener("wheel", onWheel, { passive: true });
    viewport.addEventListener("touchmove", onNavigation, { passive: true });
    document.addEventListener("pointerdown", onNavigation, true);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      activation.dismissed = true;
      activation.cancelScroll?.();
      viewport.removeEventListener("wheel", onWheel);
      viewport.removeEventListener("touchmove", onNavigation);
      document.removeEventListener("pointerdown", onNavigation, true);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [requestKey, loaded, viewport]);
  const active = target?.key === request?.key ? target : null;
  const row = active
    ? rows.find(
        (row) =>
          row.kind === "message" &&
          matchesAssistantCitationMessage(row.message, active.citation.messageId) &&
          row.message.role === "assistant",
      )
    : undefined;
  const reportedKey = useRef<string | null>(null);
  useEffect(() => {
    if (!active || row || reportedKey.current === active.key) return;
    reportedKey.current = active.key;
    toastManager.add({
      type: "warning",
      title: "The cited response is not in the loaded history",
      description: "Your selected text and comment are still saved in the quotation.",
    });
  }, [active, row]);
  return {
    target: row ? active : null,
    alwaysRender: row && positionedKey !== active?.key ? { keys: [row.id] } : undefined,
    onLoad: () => setLoaded(true),
  };
}
